import React from "react";
import { Leaf, AlertCircle, PackageOpen, BellRing } from "lucide-react";
import { SITE_IMAGES } from "../../data/siteImages";
import PlantImage from "./PlantImage";

// Shared loading / empty / error states — the plan insists every screen has
// all four states, never just the happy path.

export function ProductCardSkeleton() {
  return (
    <div className="surface overflow-hidden">
      <div className="skeleton aspect-[4/5] w-full" />
      <div className="space-y-3 p-4">
        <div className="skeleton h-4 w-3/4 rounded" />
        <div className="skeleton h-3 w-1/2 rounded" />
        <div className="skeleton h-4 w-1/4 rounded" />
      </div>
    </div>
  );
}

export function GridSkeleton({ count = 8 }) {
  return (
    <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function EmptyState({ title, message, action, image = SITE_IMAGES.emptyState }) {
  return (
    <div className="surface overflow-hidden text-center">
      {image && (
        <PlantImage
          src={image}
          alt=""
          ratio="5/2"
          className="border-b border-line"
        />
      )}
      <div className="flex flex-col items-center justify-center px-6 py-10">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-forest-100 text-forest-700">
          <Leaf size={26} />
        </span>
        <h3 className="t-h3 mb-2">{title}</h3>
        <p className="t-lead mb-6 max-w-md text-base">{message}</p>
        {action}
      </div>
    </div>
  );
}

export function ErrorState({ message = "Something went wrong on our end.", onRetry, image = SITE_IMAGES.emptyState }) {
  return (
    <div className="surface overflow-hidden text-center">
      {image && (
        <PlantImage
          src={image}
          alt=""
          ratio="5/2"
          className="border-b border-line"
        />
      )}
      <div className="flex flex-col items-center justify-center px-6 py-10">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger">
          <AlertCircle size={26} />
        </span>
        <h3 className="t-h3 mb-2">A small hiccup</h3>
        <p className="t-lead mb-6 max-w-md text-base">{message}</p>
        {onRetry && (
          <button className="btn-forest" onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

// Out-of-stock "notify me" capture — also a marketing-consent moment.
export function NotifyMe({ plantName }) {
  const [email, setEmail] = React.useState("");
  const [done, setDone] = React.useState(false);

  if (done) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-forest-100 px-4 py-3 text-sm text-forest-900">
        <BellRing size={18} />
        We'll email you the moment {plantName} is back.
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (email) setDone(true);
      }}
      className="space-y-2"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-ink-soft">
        <PackageOpen size={18} className="text-brass" />
        Back in spring — get notified
      </div>
      <div className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.ie"
          className="min-w-0 flex-1 rounded-full border border-line bg-canvas px-4 py-2.5 text-sm focus:border-forest-500"
        />
        <button type="submit" className="btn-brass whitespace-nowrap">
          Notify me
        </button>
      </div>
      <p className="text-xs text-ink-soft">
        We'll only email you about this plant. No spam, unsubscribe anytime.
      </p>
    </form>
  );
}

export function Trait({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-line bg-canvas px-3 py-2.5">
      <span className="text-forest-500">
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-ink-soft">{label}</div>
        <div className="truncate text-sm font-medium text-ink">{value}</div>
      </div>
    </div>
  );
}
